enum FilterJoinType {
  AND = '∧',
  OR = '∨'
}

export enum FilterOperatorType {
  EQ = '≡',
  NE = '≢',
  GT = '>',
  LT = '<',
  GE = '≥',
  LE = '≤',
  NOT = '¬',
  ELEMENT_OF = '∈',
  NOT_ELEMENT_OF = '∉',

  LIKE = '≈',
  NOT_LIKE = '≉',
  AND = '∧',
  OR = '∨',
  IS_NULL = 'is null',
  NOT_NULL = 'is not null',

  ANY = 'any',
  ALL = 'all',
  CUSTOM = 'custom'
}

type CustomFn = (props: UmbrellaProps) => string;

export interface FilterOperator {
  write: () => string;
}

export type UmbrellaBoxCoordinates = {
  swLat: string;
  swLng: string;
  neLat: string;
  neLng: string;
};

export type UmbrellaProps = {
  expr?: unknown;
  key?: string;
  value?: string | string[] | number | number[] | boolean | UmbrellaBoxCoordinates;
};

export class UmbrellaOperator implements FilterOperator {
  type: FilterOperatorType;
  props: UmbrellaProps;
  isEnum = false;
  customFn: CustomFn | null;

  constructor(type: FilterOperatorType, props: UmbrellaProps, isEnum = false, customFn: CustomFn | null = null) {
    this.type = type;
    this.props = props;
    this.isEnum = isEnum;
    this.customFn = customFn;
  }

  write(): string {
    switch (this.type) {
      case FilterOperatorType.NOT:
        return `${FilterOperatorType.NOT}(${this.props.expr.toString()})`;
      case FilterOperatorType.OR:
      case FilterOperatorType.AND:
        return `(${this.props.expr.toString()})`;
      case FilterOperatorType.EQ:
      case FilterOperatorType.NE:
        if (typeof this.props.value === 'string' && !this.isEnum) {
          return `${this.props.key}${this.type}'${this.props.value}'`;
        }
        return `${this.props.key}${this.type}${this.props.value}`;
      case FilterOperatorType.GT:
      case FilterOperatorType.LT:
      case FilterOperatorType.GE:
      case FilterOperatorType.LE:
        return `${this.props.key}${this.type}${this.props.value}`;
      case FilterOperatorType.LIKE:
      case FilterOperatorType.NOT_LIKE:
        return `${this.props.key}${this.type}'${this.props.value}'`;
      case FilterOperatorType.ELEMENT_OF:
      case FilterOperatorType.NOT_ELEMENT_OF:
        if (!Array.isArray(this.props.value)) {
          console.error(`Expected array for ELEMENT_OF/NOT_ELEMENT_OF, got: ${typeof this.props.value}`);
          return `${this.props.key}${this.type}{${this.props.value}}`
        }
        return `${this.props.key}${this.type}{${this.props.value
          .map((it?: string | number) => (this.isEnum ? it : `'${it}'`))
          .join(',')}}`;
      case FilterOperatorType.IS_NULL:
      case FilterOperatorType.NOT_NULL:
        return `${this.props.key} ${this.type}`;
      case FilterOperatorType.ANY:
        return `${FilterOperatorType.ANY}(${this.props.expr.toString()})`;
      case FilterOperatorType.ALL:
        return `${FilterOperatorType.ALL}(${this.props.expr.toString()})`;
      case FilterOperatorType.CUSTOM:
        return this.customFn ? this.customFn(this.props) : '';
    }
    throw new Error(`No mapping defined for ${this.type}`);
  }
}

export class FilterExpression {
  operations: FilterOperator[] = [];

  joinType: FilterJoinType = FilterJoinType.AND;

  toString() {
    return this.operations.map((it) => it.write()).join(this.joinType);
  }

  not(expr: FilterExpression): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.NOT, { expr }));
    return this;
  }

  or(expr: FilterExpression): FilterExpression {
    this.joinType = FilterJoinType.OR;
    this.operations.push(new UmbrellaOperator(FilterOperatorType.OR, { expr }));
    return this;
  }

  and(expr: FilterExpression): FilterExpression {
    this.joinType = FilterJoinType.AND;
    this.operations.push(new UmbrellaOperator(FilterOperatorType.AND, { expr }));
    return this;
  }

  equals(key: string, value: string | number | boolean, isEnum = false): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.EQ, { key, value }, isEnum));
    return this;
  }

  equalsIgnoreCase(key: string, value: string, isEnum = false): FilterExpression {
    return this.equals(key, `lower(${value})`, isEnum);
  }

  notEquals(key: string, value: string | number | boolean, isEnum = false): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.NE, { key, value }, isEnum));
    return this;
  }

  notEqualsIgnoreCase(key: string, value: string, isEnum = false): FilterExpression {
    return this.notEquals(key, `lower(${value})`, isEnum);
  }

  like(key: string, value: string): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.LIKE, { key, value }));
    return this;
  }

  likeIgnoreCase(key: string, value: string): FilterExpression {
    return this.like(key, `lower(${value})`);
  }

  notLike(key: string, value: string): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.NOT_LIKE, { key, value }));
    return this;
  }

  notLikeIgnoreCase(key: string, value: string): FilterExpression {
    return this.notLike(key, `lower(${value})`);
  }

  lessThanOrEqual(key: string, value: number | string): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.LE, { key, value }));
    return this;
  }

  greaterThanOrEqual(key: string, value: number | string): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.GE, { key, value }));
    return this;
  }

  lessThan(key: string, value: number | string): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.LT, { key, value }));
    return this;
  }

  greaterThan(key: string, value: number | string): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.GT, { key, value }));
    return this;
  }

  notContains(key: string, value: string[] | number[], isEnum = false): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.NOT_ELEMENT_OF, { key, value }, isEnum));
    return this;
  }

  contains(key: string, value: string[] | number[], isEnum = false): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.ELEMENT_OF, { key, value }, isEnum));
    return this;
  }

  custom(operator: FilterOperator): FilterExpression {
    this.operations.push(operator);
    return this;
  }

  isNull(key: string): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.IS_NULL, { key }));
    return this;
  }

  isNotNull(key: string): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.NOT_NULL, { key }));
    return this;
  }

  any(expr: FilterExpression): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.ANY, { expr }));
    return this;
  }

  all(expr: FilterExpression): FilterExpression {
    this.operations.push(new UmbrellaOperator(FilterOperatorType.ALL, { expr }));
    return this;
  }

  box(key: string, value: UmbrellaBoxCoordinates): FilterExpression {
    this.operations.push(
      new UmbrellaOperator(
        FilterOperatorType.CUSTOM,
        { key, value },
        false,
        (props) => {
          if (typeof props.value != 'object' || !('swLat' in props.value) || !('swLng' in props.value) || !('neLat' in props.value) || !('neLng' in props.value)) {
            console.log('Invalid box coordinates provided');
            return '';
          }
          return `box(${props.key},${props.value.swLat},${props.value.swLng},${props.value.neLat},${props.value.neLng})`;
        }
      )
    );
    return this;
  }
}

export const like = (field: string, value: string): FilterExpression => {
  return new FilterExpression().like(field, value);
};

export const likeIgnoreCase = (field: string, value: string): FilterExpression => {
  return new FilterExpression().likeIgnoreCase(field, value);
};

export const notLike = (field: string, value: string): FilterExpression => {
  return new FilterExpression().notLike(field, value);
};

export const notLikeIgnoreCase = (field: string, value: string): FilterExpression => {
  return new FilterExpression().notLikeIgnoreCase(field, value);
};

export const equals = (field: string, value: string | number | boolean, isEnum = false): FilterExpression => {
  return new FilterExpression().equals(field, value, isEnum);
};

export const equalsIgnoreCase = (field: string, value: string, isEnum = false): FilterExpression => {
  return new FilterExpression().equalsIgnoreCase(field, value, isEnum);
};

export const notEquals = (field: string, value: string | number, isEnum = false): FilterExpression => {
  return new FilterExpression().notEquals(field, value, isEnum);
};

export const notEqualsIgnoreCase = (field: string, value: string, isEnum = false): FilterExpression => {
  return new FilterExpression().notEqualsIgnoreCase(field, value, isEnum);
};

export const lessThanOrEqual = (field: string, value: number | string): FilterExpression => {
  return new FilterExpression().lessThanOrEqual(field, value);
};

export const greaterThanOrEqual = (field: string, value: number | string): FilterExpression => {
  return new FilterExpression().greaterThanOrEqual(field, value);
};

export const lessThan = (field: string, value: number | string): FilterExpression => {
  return new FilterExpression().lessThan(field, value);
};

export const greaterThan = (field: string, value: number | string): FilterExpression => {
  return new FilterExpression().greaterThan(field, value);
};

export const contains = (field: string, value: string[] | number[], isEnum = false): FilterExpression => {
  return new FilterExpression().contains(field, value, isEnum);
};

export const notContains = (field: string, value: string[] | number[], isEnum = false): FilterExpression => {
  return new FilterExpression().notContains(field, value, isEnum);
};

export const isNull = (field: string): FilterExpression => {
  return new FilterExpression().isNull(field);
};

export const isNotNull = (field: string): FilterExpression => {
  return new FilterExpression().isNotNull(field);
};

export const not = (expr: FilterExpression): FilterExpression => {
  return new FilterExpression().not(expr);
};

export const any = (expr: FilterExpression): FilterExpression => {
  return new FilterExpression().any(expr);
};

export const all = (expr: FilterExpression): FilterExpression => {
  return new FilterExpression().all(expr);
};
